/**
 * アカウント(登録・ログイン)の抽象(docs/10_paltown_platform.md §3.1)。
 *
 *   - ログインなしでもタイピングは遊べる(匿名Auth。現行どおり)
 *   - 登録・ログインは **メール+パスワード / Google**(MVP)
 *   - 匿名 → 登録は **アカウントリンク**で同一UIDのまま昇格(進捗・コイン・アイテムを失わない)
 *
 * **このファイルは Firebase SDK を import しない。**
 * 画面(`ui/AccountScreen.tsx` / `store/authStore.ts`)はここの型しか知らないので、
 * ローカルモードのバンドルに SDK が1バイトも混ざらない(CLAUDE.md ルール6)。
 * 実装は `firebase/authService.ts`(動的 import チャンクの向こう側)。
 */

/** Firebase の providerId をそのまま使う(将来 apple.com / oidc.line を足す)。 */
export type AuthProviderId = 'password' | 'google.com' | (string & {});

/** 画面に出すぶんだけに絞ったユーザー。SDK の `User` は外へ出さない。 */
export interface AccountUser {
  readonly uid: string;
  /** 匿名(= まだ登録していない)なら true。 */
  readonly isAnonymous: boolean;
  readonly email: string | null;
  readonly displayName: string | null;
  /** 紐付いているログイン手段。匿名だけのときは空。 */
  readonly providers: readonly AuthProviderId[];
}

/**
 * 認証の失敗種別。
 *
 * SDK のコード(`auth/...`)をそのまま画面に出さないための翻訳先。
 * `account-exists` だけは分岐(「そのアカウントでログインし直すか」)に使う。
 */
export type AuthErrorCode =
  | 'invalid-email'
  | 'weak-password'
  | 'email-already-in-use'
  | 'invalid-credential'
  | 'user-not-found'
  | 'too-many-requests'
  | 'network'
  /** その Google アカウントは既に別ユーザーのもの。匿名の進捗は引き継げない。 */
  | 'account-exists'
  /** ポップアップを閉じた/連打した。エラー表示は出さず黙って戻す。 */
  | 'popup-cancelled'
  /** ローカルモード等、そもそも登録機能が使えない。 */
  | 'unsupported'
  | 'unknown';

export class AuthError extends Error {
  constructor(
    readonly code: AuthErrorCode,
    message: string,
    override readonly cause?: unknown,
  ) {
    super(message);
    this.name = 'AuthError';
  }
}

/**
 * 画面に出す文言(小学生も読むのでひらがな中心。ただし操作するのは保護者)。
 * 原因が分かるものだけ具体的に言い、それ以外は「もういちど」に寄せる。
 */
const MESSAGES: Readonly<Record<AuthErrorCode, string>> = {
  'invalid-email': 'メールアドレスの かたちが ちがうみたい。もういちど かくにんしてね。',
  'weak-password': 'パスワードは 6もじ いじょうに してね。',
  'email-already-in-use':
    'この メールアドレスは もう つかわれています。「ログイン」から はいってね。',
  'invalid-credential': 'メールアドレスか パスワードが ちがうみたい。',
  'user-not-found': 'この メールアドレスの アカウントが 見つかりません。',
  'too-many-requests': 'なんかい も しっぱいしたので、しばらく まってから ためしてね。',
  network: 'インターネットに つながらないみたい。つながってから もういちど ためしてね。',
  'account-exists': 'この アカウントは もう つかわれています。',
  'popup-cancelled': 'ログインを やめました。',
  unsupported: 'この かんきょうでは とうろく・ログインが つかえません。',
  unknown: 'うまく いきませんでした。もういちど ためしてね。',
};

export function authErrorMessage(error: unknown): string {
  if (error instanceof AuthError) return MESSAGES[error.code];
  return MESSAGES.unknown;
}

/**
 * 認証操作の結果。
 *
 * `redirecting` はポップアップが使えない環境でリダイレクト認証へ切り替えた状態
 * (このあとページごと離脱するので、呼び出し側は待たずに何もしない)。
 */
export type AuthOutcome =
  { readonly kind: 'signedIn'; readonly user: AccountUser } | { readonly kind: 'redirecting' };

export interface AuthService {
  /** 登録・ログインが使えるか(ローカルモードでは false = 導線ごと出さない)。 */
  readonly supported: boolean;
  /** 現在のユーザー(未サインイン・未解決なら null)。 */
  current(): AccountUser | null;
  /** 変化の購読。登録直後に現在値を1回流す。戻り値で解除。 */
  subscribe(listener: (user: AccountUser | null) => void): () => void;
  /**
   * リダイレクト認証の続き(起動時に1回だけ呼ぶ)。
   * 何も無ければ null。衝突していれば `AuthError('account-exists')`。
   */
  completeRedirect(): Promise<AccountUser | null>;
  /**
   * 新規登録。匿名で遊んでいたら **リンクして同一UIDのまま昇格**する。
   * 既に使われているメールなら `AuthError('email-already-in-use')`。
   */
  registerWithEmail(email: string, password: string): Promise<AuthOutcome>;
  /** 既存アカウントへログイン(いまの匿名の記録は引き継がれない)。 */
  signInWithEmail(email: string, password: string): Promise<AuthOutcome>;
  /**
   * Google。匿名なら `linkWithPopup` で昇格、ポップアップ不可なら redirect。
   * その Google アカウントが既存ユーザーなら `AuthError('account-exists')` を投げ、
   * `continueWithExistingAccount()` で乗り換えられる状態にする。
   */
  signInWithGoogle(): Promise<AuthOutcome>;
  /**
   * `account-exists` のあとに「そっちの アカウントで ログインする」を選んだとき。
   * **匿名で遊んでいたぶんは引き継がれない**(UI で必ず伝えること)。
   */
  continueWithExistingAccount(): Promise<AuthOutcome>;
  /** 衝突の保留を捨てる(「やっぱりやめる」)。 */
  discardExistingAccount(): void;
  /** ログアウト。そのあとゲームを続けられるよう匿名で入り直す。 */
  signOut(): Promise<void>;
}

/**
 * ローカルモード用(= 何もできない)実装。
 *
 * `supported: false` なので画面は導線ごと出さない。
 * それでも呼ばれたら `unsupported` で失敗するだけでゲームは止めない。
 */
export class UnavailableAuthService implements AuthService {
  readonly supported = false;

  current(): AccountUser | null {
    return null;
  }

  subscribe(listener: (user: AccountUser | null) => void): () => void {
    listener(null);
    return () => {};
  }

  async completeRedirect(): Promise<AccountUser | null> {
    return null;
  }

  async registerWithEmail(): Promise<AuthOutcome> {
    throw unavailable();
  }

  async signInWithEmail(): Promise<AuthOutcome> {
    throw unavailable();
  }

  async signInWithGoogle(): Promise<AuthOutcome> {
    throw unavailable();
  }

  async continueWithExistingAccount(): Promise<AuthOutcome> {
    throw unavailable();
  }

  discardExistingAccount(): void {}

  async signOut(): Promise<void> {}
}

function unavailable(): AuthError {
  return new AuthError('unsupported', 'auth is not available in local mode');
}

/** ログイン手段の表示名(マイアカウント欄)。 */
export function providerLabel(providers: readonly AuthProviderId[]): string {
  if (providers.includes('google.com')) return 'Google';
  if (providers.includes('password')) return 'メールアドレス';
  return 'ゲスト';
}

/** 「だれとして 遊んでいるか」の表示名。 */
export function accountLabel(user: AccountUser): string {
  return user.displayName ?? user.email ?? 'ゲスト';
}
